import { storage } from '@/utils/storageManager';
import { getUserStorageKey } from '@/hooks/campaign/utils/storageUtils';
import type { DraftData } from './DraftTypes';

// Centralized storage keys
class StorageKeys {
  static readonly DRAFTS_KEY = 'campaign_drafts';
  static readonly CURRENT_DRAFT_KEY = 'current_draft_id';
  
  static getUserDraftsKey(): string {
    return getUserStorageKey(this.DRAFTS_KEY);
  }
  
  static getUserCurrentDraftKey(): string {
    return getUserStorageKey(this.CURRENT_DRAFT_KEY);
  }
  
  static getAllDraftKeys(): string[] {
    const keys = [this.DRAFTS_KEY, this.getUserDraftsKey()];
    
    // Scan for additional draft keys in localStorage
    if (typeof localStorage !== 'undefined') {
      try {
        Object.keys(localStorage).forEach((key) => {
          if (key.startsWith(this.DRAFTS_KEY) && !keys.includes(key)) {
            keys.push(key);
          }
        });
      } catch (error) {
        console.warn('Error scanning localStorage keys:', error);
      }
    }
    
    return keys;
  }
  
  static getAllCurrentDraftKeys(): string[] {
    const keys = [this.CURRENT_DRAFT_KEY, this.getUserCurrentDraftKey()];
    
    if (typeof localStorage !== 'undefined') {
      try {
        Object.keys(localStorage).forEach((key) => {
          if (key.startsWith(this.CURRENT_DRAFT_KEY) && !keys.includes(key)) {
            keys.push(key);
          }
        });
      } catch (error) {
        console.warn('Error scanning current draft keys:', error);
      }
    }
    
    return keys;
  }
}

// Safe JSON parsing with proper error handling
const parseStored = <T>(raw: string | null, fallback: T): T => {
  if (!raw) return fallback;
  
  try {
    const parsed = JSON.parse(raw);
    
    // Handle storageManager envelope format
    if (parsed && typeof parsed === 'object' && 'value' in parsed) {
      return parsed.value as T;
    }
    
    return parsed as T;
  } catch (error) {
    console.warn('Failed to parse stored data:', error);
    return fallback;
  }
};

// Read drafts from a specific storage key
const readDraftsFromKey = (key: string): DraftData[] => {
  try {
    // Try storage manager first
    const viaManager = storage.getItem<DraftData[]>(key);
    if (Array.isArray(viaManager)) {
      return viaManager.filter(draft => draft?.id); // Filter out invalid entries
    }
    
    // Fallback to direct localStorage access
    if (typeof localStorage !== 'undefined') {
      const raw = localStorage.getItem(key);
      const parsed = parseStored<DraftData[]>(raw, []);
      return parsed.filter(draft => draft?.id); // Filter out invalid entries
    }
    
    return [];
  } catch (error) {
    console.warn(`Error reading drafts from key ${key}:`, error);
    return [];
  }
};

// Validate draft data structure
const isValidDraft = (draft: any): draft is DraftData => {
  return draft && 
         typeof draft === 'object' && 
         typeof draft.id === 'string' && 
         typeof draft.name === 'string' &&
         typeof draft.currentStep === 'number' &&
         Array.isArray(draft.selectedLocations);
};

export class DraftRepository {
  list(): DraftData[] {
    console.log('📋 DRAFT-REPO: Listing all drafts...');
    
    const keysToRead = StorageKeys.getAllDraftKeys();
    const map = new Map<string, DraftData>();

    keysToRead.forEach((key) => {
      const drafts = readDraftsFromKey(key);
      console.log(`📋 DRAFT-REPO: Found ${drafts.length} drafts in key: ${key}`);
      
      drafts.forEach((draft) => {
        if (!isValidDraft(draft)) {
          console.warn(`📋 DRAFT-REPO: Invalid draft structure found:`, draft);
          return;
        }
        
        const existing = map.get(draft.id);
        if (!existing) {
          map.set(draft.id, draft);
        } else {
          // Keep the most recently updated version
          const existingTime = new Date(existing.updatedAt || existing.createdAt || 0).getTime();
          const newTime = new Date(draft.updatedAt || draft.createdAt || 0).getTime();
          
          if (newTime >= existingTime) {
            console.log(`📋 DRAFT-REPO: Updating draft ${draft.id} with newer version`);
            map.set(draft.id, draft);
          }
        }
      });
    });

    const merged = Array.from(map.values());
    const sorted = merged.sort((a, b) => {
      const timeA = new Date(a.updatedAt || a.createdAt).getTime();
      const timeB = new Date(b.updatedAt || b.createdAt).getTime();
      return timeB - timeA; // Most recent first
    });
    
    console.log(`📋 DRAFT-REPO: Returning ${sorted.length} total drafts`);
    return sorted;
  }

  get(id: string): DraftData | null {
    if (!id) return null;
    
    console.log(`📋 DRAFT-REPO: Getting draft with ID: ${id}`);
    const draft = this.list().find((d) => d.id === id) || null;
    
    if (draft) {
      console.log(`📋 DRAFT-REPO: Found draft: ${draft.name}`);
    } else {
      console.log(`📋 DRAFT-REPO: Draft not found: ${id}`);
    }
    
    return draft;
  }

  upsert(draft: DraftData): void {
    if (!isValidDraft(draft)) {
      console.error('📋 DRAFT-REPO: Attempted to save invalid draft:', draft);
      return;
    }
    
    console.log(`📋 DRAFT-REPO: Upserting draft: ${draft.id} - ${draft.name}`);
    
    const existing = this.get(draft.id);
    const now = new Date().toISOString();
    
    // Preserve creation time, update modification time
    const finalDraft: DraftData = {
      ...draft,
      createdAt: existing?.createdAt || draft.createdAt || now,
      updatedAt: now
    };

    const writeToKey = (key: string) => {
      try {
        const drafts = readDraftsFromKey(key);
        const index = drafts.findIndex((d) => d.id === finalDraft.id);
        
        if (index >= 0) {
          drafts[index] = finalDraft;
          console.log(`📋 DRAFT-REPO: Updated existing draft in ${key}`);
        } else {
          drafts.push(finalDraft);
          console.log(`📋 DRAFT-REPO: Added new draft to ${key}`);
        }
        
        storage.setItemImmediate(key, drafts);
      } catch (error) {
        console.error(`📋 DRAFT-REPO: Error writing to key ${key}:`, error);
      }
    };

    // Write to both generic and user-specific keys for redundancy
    writeToKey(StorageKeys.DRAFTS_KEY);
    writeToKey(StorageKeys.getUserDraftsKey());
    
    console.log(`📋 DRAFT-REPO: Successfully saved draft: ${finalDraft.id}`);
  }

  remove(id: string): void {
    if (!id) return;
    
    console.log(`📋 DRAFT-REPO: Removing draft: ${id}`);
    
    const writeFiltered = (key: string) => {
      try {
        const drafts = readDraftsFromKey(key).filter((d) => d.id !== id);
        storage.setItemImmediate(key, drafts);
        console.log(`📋 DRAFT-REPO: Removed draft from ${key}`);
      } catch (error) {
        console.error(`📋 DRAFT-REPO: Error removing from key ${key}:`, error);
      }
    };
    
    // Remove from all draft storage keys
    StorageKeys.getAllDraftKeys().forEach(writeFiltered);
  }

  getCurrentId(): string | null {
    console.log('📋 DRAFT-REPO: Getting current draft ID...');
    
    // Try direct storage manager access first
    const keys = StorageKeys.getAllCurrentDraftKeys();
    
    for (const key of keys) {
      try {
        const direct = storage.getItem<string>(key);
        if (direct) {
          console.log(`📋 DRAFT-REPO: Found current draft ID in ${key}: ${direct}`);
          return direct;
        }
      } catch (error) {
        console.warn(`📋 DRAFT-REPO: Error reading from ${key}:`, error);
      }
    }

    // Fallback: scan all possible current draft keys in localStorage
    let best: { id: string; timestamp: number } | null = null;
    
    if (typeof localStorage !== 'undefined') {
      try {
        Object.keys(localStorage).forEach((key) => {
          if (key.startsWith(StorageKeys.CURRENT_DRAFT_KEY)) {
            const raw = localStorage.getItem(key);
            if (!raw) return;
            
            try {
              const parsed = JSON.parse(raw);
              const value = parsed && typeof parsed === 'object' && 'value' in parsed ? parsed.value : parsed;
              const timestamp = parsed && typeof parsed === 'object' && 'timestamp' in parsed 
                ? Number(parsed.timestamp) : 0;
              const id = String(value || '');
              
              if (id && (!best || timestamp > best.timestamp)) {
                best = { id, timestamp };
              }
            } catch {
              // Treat as raw string
              const id = String(raw);
              if (id && !best) {
                best = { id, timestamp: 0 };
              }
            }
          }
        });
        
        // Check legacy key
        const legacy = localStorage.getItem('draftCampaignId');
        if (legacy && !best) {
          best = { id: legacy, timestamp: 0 };
        }
      } catch (error) {
        console.warn('📋 DRAFT-REPO: Error scanning localStorage:', error);
      }
    }

    const result = best?.id || null;
    console.log(`📋 DRAFT-REPO: Current draft ID resolved to: ${result}`);
    return result;
  }

  setCurrentId(id: string): void {
    if (!id) return;
    
    console.log(`📋 DRAFT-REPO: Setting current draft ID: ${id}`);
    
    try {
      // Set in both generic and user-specific keys
      storage.setItemImmediate(StorageKeys.CURRENT_DRAFT_KEY, id);
      storage.setItemImmediate(StorageKeys.getUserCurrentDraftKey(), id);
      
      // Set legacy key for backward compatibility
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem('draftCampaignId', id);
      }
      
      console.log(`📋 DRAFT-REPO: Successfully set current draft ID: ${id}`);
    } catch (error) {
      console.error('📋 DRAFT-REPO: Error setting current draft ID:', error);
    }
  }

  clearCurrentId(): void {
    console.log('📋 DRAFT-REPO: Clearing current draft ID');
    
    try {
      // Clear all current draft keys
      StorageKeys.getAllCurrentDraftKeys().forEach(key => {
        storage.removeItem(key);
      });
      
      // Clear legacy key
      if (typeof localStorage !== 'undefined') {
        localStorage.removeItem('draftCampaignId');
      }
      
      console.log('📋 DRAFT-REPO: Successfully cleared current draft ID');
    } catch (error) {
      console.error('📋 DRAFT-REPO: Error clearing current draft ID:', error);
    }
  }
}

export const draftRepository = new DraftRepository();
